/**
 * StoreActionRegistry — host-side allowlist of named handlers that cogo JSON
 * flows can invoke via the `dispatchStoreAction` builtin.
 *
 * Handler names are declared explicitly by host code (Studio / commerce app
 * / etc). Flow JSON references handlers only by name — never by function
 * reference — and unknown names are a no-op with a `console.warn` at runtime,
 * so a buggy flow cannot crash the app nor execute arbitrary code.
 *
 * See .planning/studio-shell-binding-strategy.md §3.B for the Phase 3
 * shell-as-cogo-app motivation. Typical usage:
 *
 *   const registry = new StoreActionRegistry()
 *   registry.registerAll({
 *     toggleForceMock:      () => useStudioStore.getState().toggleForceMock(),
 *     toggleDiagnosticPanel: () => useStudioStore.getState().toggleDiagnosticPanel(),
 *   })
 *   actionFlowEngine.setStoreActionRegistry(registry)
 *
 * And the flow JSON:
 *
 *   { "type": "basic", "action": "dispatchStoreAction",
 *     "params": { "handler": "toggleForceMock" } }
 *
 * Handlers receive the resolved `params.params` object as their argument (may
 * be undefined if the caller omitted the nested field). Return values are
 * ignored; async handlers are awaited.
 */
export type StoreActionHandler = (
  params?: Record<string, unknown>,
) => void | Promise<void>

export class StoreActionRegistry {
  private handlers = new Map<string, StoreActionHandler>()

  /** Register a single named handler. Overrides existing registration. */
  register(name: string, handler: StoreActionHandler): void {
    this.handlers.set(name, handler)
  }

  /** Register multiple handlers at once. Shallow merge — existing names with
   * no entry in `handlers` are preserved. Keys with `undefined` values are
   * ignored (convenience for optional handlers). */
  registerAll(handlers: Record<string, StoreActionHandler | undefined>): void {
    for (const [name, handler] of Object.entries(handlers)) {
      if (typeof handler === "function") {
        this.handlers.set(name, handler)
      }
    }
  }

  /** Remove a previously registered handler. No-op if not present. */
  unregister(name: string): void {
    this.handlers.delete(name)
  }

  /** Clear all handlers. Mainly for test isolation. */
  clear(): void {
    this.handlers.clear()
  }

  has(name: string): boolean {
    return this.handlers.has(name)
  }

  get(name: string): StoreActionHandler | undefined {
    return this.handlers.get(name)
  }

  /** Sorted list of registered handler names — useful for validator hints
   * and DevTools inspection. */
  list(): string[] {
    return [...this.handlers.keys()].sort()
  }
}
